import dayjs from "@shared/dayjs";
import { sortByKey } from "../../shared/Utilities";

const timeWithZoneFormat = "hh:mm a z";
const timeFormat = "HH:mm";

const priorityOptions = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
];

const recurringFrequencyOptions = [
  { value: "never", label: "Never" },
  { value: "daily", label: "Daily" },
  { value: "every", label: "Every __ Days" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const weeklyOptions = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const monthlyOptions = Array(31)
  .fill()
  .map((_, i) => {
    const day = i + 1;

    return { value: day, label: `Day ${day}` };
  });

const timeUnitOptions = [
  { value: "seconds", label: "Seconds" },
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const timeUnitOptionsForEvents = [{ value: "days", label: "Days" }];

const delayFactorOptions = [
  { value: -1, label: "Before" },
  { value: 1, label: "After" },
];

const recurringFrequencyTypes = [
  { value: "never", label: "Never" },
  { value: "number", label: "Every _ Days" },
  { value: "weekly", label: "Weekly" },
];

const ANY_STATUS_VALUE = "any";

const parseAutoPauseStatuses = (value) =>
  value == null
    ? []
    : String(value)
        .split(",")
        .map((status) => status.trim())
        .filter(Boolean);

const serializeAutoPauseStatuses = (values) => values.join(",");

const peopleAutoPauseStatusOptions = [
  { label: "Any Status", value: "any" },
  { label: "New", value: "new" },
  { label: "Unqualified", value: "unqualified" },
  { label: "Watch", value: "watch" },
  { label: "Nurture", value: "nurture" },
  { label: "Hot", value: "hot" },
  { label: "Past (Client)", value: "past" },
  { label: "Inactive", value: "inactive" },
  { label: "Trash", value: "trash" },
  { label: "Archived", value: "archived" },
];

const transactionAutoPauseStatusOptions = [
  { label: "Any Status", value: "any" },
  { label: "Pipeline", value: "pipeline" },
  { label: "Coming Soon", value: "coming soon" },
  { label: "Active", value: "active" },
  { label: "Pending", value: "pending" },
  { label: "Closed", value: "sold" },
  { label: "Expired", value: "expired" },
  { label: "Withdrawn", value: "withdrawn" },
  { label: "Canceled", value: "canceled" },
  { label: "Archived", value: "archived" },
];

const peopleCompletionStatusOptions = [
  { label: "Don't Change", value: "" },
  { label: "New", value: "new" },
  { label: "Unqualified", value: "unqualified" },
  { label: "Watch", value: "watch" },
  { label: "Nurture", value: "nurture" },
  { label: "Hot", value: "hot" },
  { label: "Past (Client)", value: "past" },
  { label: "Inactive", value: "inactive" },
];

const transactionCompletionStatusOptions = [
  { label: "Don't Change", value: "" },
  { label: "Pipeline", value: "pipeline" },
  { label: "Coming Soon", value: "coming soon" },
  { label: "Active", value: "active" },
  { label: "Pending", value: "pending" },
  { label: "Closed", value: "sold" },
  { label: "Expired", value: "expired" },
  { label: "Withdrawn", value: "withdrawn" },
  { label: "Canceled", value: "canceled" },
  { label: "Archived", value: "archived" },
];

const canBeContingent = (scheduleType, triggerOptions = []) => {
  if (scheduleType === "event") {
    return false;
  }

  return triggerOptions.filter((option) => !option.disabled && option.value !== "Trigger").length > 0;
};

const buildTimeOptions = (increment) => {
  const totalMinutes = 1440;

  const options = Array(totalMinutes / increment)
    .fill()
    .map((_, i) => {
      const minutes = (i + 1) * increment;
      const time = dayjs().startOf("year").add(minutes, "minute");
      const label = time.tz(Brivity.LocalTimeZone).format(timeWithZoneFormat);
      const value = time.utc().format(timeFormat);
      const valueForSort = time.tz(Brivity.LocalTimeZone).format(timeFormat);

      return { value, label, valueForSort };
    });
  return sortByKey(options, "valueForSort");
};

// Building the options is expensive: each one costs several dayjs constructions and two timezone
// conversions, which adds up to a few milliseconds for the whole list. Since the result only depends
// on the increment and the viewer's timezone, cache it. Callers also rely on the identity being
// stable so that passing it as react-select's `options` doesn't invalidate the dropdown every
// render. Nothing mutates the returned array.
const timeOptionsCache = new Map();

const timeOptionsCacheEntry = (increment) => {
  const cacheKey = `${increment}|${Brivity.LocalTimeZone}`;
  let entry = timeOptionsCache.get(cacheKey);

  if (!entry) {
    const options = buildTimeOptions(increment);
    entry = {
      options,
      // `run_time` reaches us as either a string or a number depending on the caller, so key the
      // lookup on the stringified value to keep the previous loose-equality matching behavior.
      byValue: new Map(options.map((opt) => [String(opt.value), opt])),
    };
    timeOptionsCache.set(cacheKey, entry);
  }

  return entry;
};

const timeOptions = (increment = 15) => timeOptionsCacheEntry(increment).options;

const findTimeOptionByValue = (value, increment = 15) =>
  timeOptionsCacheEntry(increment).byValue.get(String(value));

const findDefaultRunAfterTimeOption = (type) => {
  const defaultTime = type === "Task" ? "17:00" : "09:00";

  return timeOptions().find((e) => e.valueForSort === defaultTime);
};

const shortDelayUnits = {
  seconds: "sec",
  minutes: "min",
  hours: "hours",
  days: "days",
};

const getActionById = (id, autoPlan) => autoPlan.actions.filter((action) => action.id === id)[0];

const autoPlanNameLabel = (id, autoPlans = []) => {
  const autoPlan = autoPlans.filter((a) => a.id === id)[0];
  return autoPlan ? autoPlan.name : "";
};

export {
  priorityOptions,
  recurringFrequencyOptions,
  weeklyOptions,
  monthlyOptions,
  canBeContingent,
  timeOptions,
  findTimeOptionByValue,
  timeUnitOptionsForEvents,
  timeUnitOptions,
  delayFactorOptions,
  recurringFrequencyTypes,
  getActionById,
  autoPlanNameLabel,
  shortDelayUnits,
  timeWithZoneFormat,
  ANY_STATUS_VALUE,
  parseAutoPauseStatuses,
  serializeAutoPauseStatuses,
  peopleAutoPauseStatusOptions,
  peopleCompletionStatusOptions,
  transactionAutoPauseStatusOptions,
  transactionCompletionStatusOptions,
  findDefaultRunAfterTimeOption,
};
